import {
  Invoice,
  Payment,
  PaymentMethod,
  PublicInvoiceView,
  SquareConnectionStatus,
  SquareLocation,
  StripeConnectionStatus,
  StripeIntent,
} from '../../types';
import api from './client';

/**
 * The payment ledger. Invoice status is derived server-side from these rows —
 * there is no "mark paid" endpoint; recording a payment IS marking it paid.
 */
export const paymentsService = {
  /** Omitting `amount` records the full outstanding balance. */
  recordExternal: async (payload: {
    invoiceId: string;
    amount?: number;
    method: PaymentMethod;
    note?: string;
    receivedAt?: number;
  }): Promise<Invoice> => {
    const response = await api.post<Invoice>('/payments', payload);
    return response.data;
  },
  listForInvoice: async (invoiceId: string): Promise<Payment[]> => {
    const response = await api.get<Payment[]>('/payments', {
      params: { invoiceId },
    });
    return response.data;
  },
  /**
   * Refunds a card payment through Square. Omit `amount` to give back
   * everything still refundable. Cash and cheque corrections use
   * `voidPayment` instead — no money moved through Square for those.
   */
  refundPayment: async (
    paymentId: string,
    payload?: { amount?: number; reason?: string },
  ): Promise<Invoice> => {
    const response = await api.post<Invoice>(
      `/payments/${paymentId}/refund`,
      payload ?? {},
    );
    return response.data;
  },
  voidPayment: async (paymentId: string): Promise<Invoice> => {
    const response = await api.post<Invoice>(`/payments/${paymentId}/void`);
    return response.data;
  },
  /** Pushes the outstanding balance to the shop's Square Terminal; resolves via webhook. */
  createTerminalCheckout: async (payload: {
    invoiceId: string;
    deviceId?: string;
  }): Promise<Payment> => {
    const response = await api.post<Payment>('/payments/terminal-checkout', payload);
    return response.data;
  },
  cancelTerminalCheckout: async (paymentId: string): Promise<Invoice> => {
    const response = await api.post<Invoice>(`/payments/${paymentId}/cancel-terminal`);
    return response.data;
  },
};

/** Unauthenticated pay-page endpoints — customers arrive from the email link. */
export const publicPaymentsService = {
  getPayView: async (invoiceId: string): Promise<PublicInvoiceView> => {
    const response = await api.get<PublicInvoiceView>(`/pay/${invoiceId}`);
    return response.data;
  },
  // No amount is ever sent — the server charges the outstanding balance.
  pay: async (
    invoiceId: string,
    payload: { sourceId: string; verificationToken?: string },
  ): Promise<PublicInvoiceView> => {
    const response = await api.post<PublicInvoiceView>(`/pay/${invoiceId}`, payload);
    return response.data;
  },
  /** Stripe shops: a PaymentIntent for the balance, confirmed by Stripe.js. */
  createStripeIntent: async (invoiceId: string): Promise<StripeIntent> => {
    const response = await api.post<StripeIntent>(`/pay/${invoiceId}/intent`);
    return response.data;
  },
  /** After Stripe.js confirms; the server re-reads the intent from Stripe. */
  confirmStripePayment: async (
    invoiceId: string,
    paymentIntentId: string,
  ): Promise<PublicInvoiceView> => {
    const response = await api.post<PublicInvoiceView>(`/pay/${invoiceId}/confirm`, {
      paymentIntentId,
    });
    return response.data;
  },
};

/**
 * Per-shop Square connection. `shopId` is only consulted for super admins —
 * shop admins always act on their own shop server-side.
 */
export const squarePaymentsService = {
  getStatus: async (shopId?: string): Promise<SquareConnectionStatus> => {
    const response = await api.get<SquareConnectionStatus>(
      '/payments/square/status',
      { params: { shopId } },
    );
    return response.data;
  },
  /** Returns the Square authorize URL; navigate the browser to it. */
  getConnectUrl: async (shopId?: string): Promise<string> => {
    const response = await api.get<{ url: string }>(
      '/payments/square/connect-url',
      { params: { shopId } },
    );
    return response.data.url;
  },
  getLocations: async (shopId?: string): Promise<SquareLocation[]> => {
    const response = await api.get<SquareLocation[]>(
      '/payments/square/locations',
      { params: { shopId } },
    );
    return response.data;
  },
  setLocation: async (
    payload: { locationId: string; deviceId?: string },
    shopId?: string,
  ): Promise<SquareConnectionStatus> => {
    const response = await api.post<SquareConnectionStatus>(
      '/payments/square/location',
      payload,
      { params: { shopId } },
    );
    return response.data;
  },
  disconnect: async (shopId?: string): Promise<void> => {
    await api.delete('/payments/square/connection', { params: { shopId } });
  },
};

/**
 * Per-shop Stripe connection. Keys go in once and never come back; the status
 * carries only the publishable key and non-secret settings.
 */
export const stripePaymentsService = {
  getStatus: async (shopId?: string): Promise<StripeConnectionStatus> => {
    const response = await api.get<StripeConnectionStatus>('/payments/stripe/status', {
      params: { shopId },
    });
    return response.data;
  },
  connect: async (
    payload: {
      secretKey: string;
      publishableKey: string;
      webhookSecret?: string;
      readerId?: string;
      primary?: boolean;
    },
    shopId?: string,
  ): Promise<StripeConnectionStatus> => {
    const response = await api.put<StripeConnectionStatus>(
      '/payments/stripe/connection',
      payload,
      { params: { shopId } },
    );
    return response.data;
  },
  updateSettings: async (
    payload: { readerId?: string; primary?: boolean; webhookSecret?: string },
    shopId?: string,
  ): Promise<StripeConnectionStatus> => {
    const response = await api.patch<StripeConnectionStatus>(
      '/payments/stripe/connection',
      payload,
      { params: { shopId } },
    );
    return response.data;
  },
  disconnect: async (shopId?: string): Promise<void> => {
    await api.delete('/payments/stripe/connection', { params: { shopId } });
  },
};
